import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Straight from '../../../assets/images/pluvaAutoLanding/rtk_straight.gif';
import Reverse from '../../../assets/images/pluvaAutoLanding/rtk_reverse.gif';
import UTurn from '../../../assets/images/pluvaAutoLanding/rtk_u-turn.gif';
import AutoTurn from '../../../assets/images/pluvaAutoLanding/rtk_auto-turn.gif';
import DriveModeObject from '../../../assets/images/pluvaAutoLanding/drive-mode_object.jpg';
import { PointBadge } from './PointBadge';

interface TechnicalDescriptionProps {
  title: string;
  gifImage: string;
  description: string;
}

const technicalDescriptions: TechnicalDescriptionProps[] = [
  {
    title: '트랙터를 사용한 자동 유턴과 골띄기',
    gifImage: UTurn,
    description:
      '트랙터 자율주행을 사용한 골띄기 작업이면 깜깜한 밤에 작업해도 문제 없어요. 야간 작업에도 4~6두둑은 더 나올 수 있도록 도와드릴게요.',
  },
  {
    title: '이앙기를 사용한 직진과 자동 선회',
    gifImage: AutoTurn,
    description:
      '집중해도 자꾸만 삐뚫어지고, 모판도 갈아야 하는 피곤한 이앙기 작업. 운전은 플루바 오토가 대신 할게요. 모판만 갈아주세요. ',
  },
  {
    title: 'A/B 직진 기능',
    gifImage: Straight,
    description:
      '플루바 오토를 설치하면 직진은 식은죽 먹기죠. 신경 곤두 세울 일이 없어요. 자율주행 시스템을 믿고 좌석에 앉아 쉬어도 문제 없습니다.',
  },
  {
    title: 'A/B 후진 기능',
    gifImage: Reverse,
    description:
      '직진 운전을 대신 해드리는데 당연히 후진도 가능하죠. 목과 허리에 무리 갈일 없도록 자율주행으로 운전 해드릴게요.',
  },
];

const useLazyLoading = (targetRef: React.RefObject<HTMLImageElement>) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    const observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          setIsVisible(true);
        }
      });
    });

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, [targetRef]);

  return isVisible;
};

const TechnicalDescription = ({ detail }: { detail: TechnicalDescriptionProps }) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const isVisible = useLazyLoading(imgRef);
  const { title, gifImage, description } = detail;

  return (
    <TechnicalDescriptionCard>
      <TechnicalDescriptionImage src={isVisible ? gifImage : undefined} ref={imgRef} />
      <TechnicalContent>
        <TechnicalTitle>{title}</TechnicalTitle>
        <TechnicalContentText>{description}</TechnicalContentText>
      </TechnicalContent>
    </TechnicalDescriptionCard>
  );
};

export const DriveModeSection = () => {
  return (
    <Container>
      <TitleSection>
        <PointBadge title={'주행 모드'} />
        <Title>{'농지를 꽉 채워 줄\n정밀하고 다양한 주행 모드'}</Title>
        <Image src={DriveModeObject} />
        <TitleHelperText>
          {'유료 RTK-GPS를 이용하여\n다양하고 정밀한 기능을 제공함은 기본,\n지속적으로 연구 및 기능 출시 중!'}
        </TitleHelperText>
      </TitleSection>

      <ContentSection>
        <TechnicalDescriptionContainer>
          <TechnicalDescriptionList>
            {technicalDescriptions.map((technicalDetail) => {
              return <TechnicalDescription key={technicalDetail.title} detail={technicalDetail} />;
            })}
          </TechnicalDescriptionList>
        </TechnicalDescriptionContainer>
      </ContentSection>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  padding: 0px 24px 48px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;

  white-space: pre-line;
`;

const Title = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XXX-Large/xxxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */
`;

const Image = styled.img`
  width: 180px;
`;

const TitleHelperText = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  white-space: pre-line;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const TechnicalDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const TechnicalDescriptionList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const TechnicalDescriptionCard = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
`;

const TechnicalDescriptionImage = styled.img`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-black, #232429);
`;

const TechnicalContent = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const TechnicalTitle = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const TechnicalContentText = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;
